/**
 * Trovva — Client Portal (Read-Only)
 *
 * Public-facing page for clients to view their deal status and documents.
 * Accessed via magic link: /portal?token=xxx
 *
 * Flow:
 *   1. Client clicks magic link in email → /portal?token=abc123
 *   2. Page exchanges magic token for 24hr session token via POST /api/client-portal
 *   3. Session token stored in memory (NOT localStorage) and used for subsequent requests
 *   4. Client sees: deal progress bar, shared documents, timeline, agent contact info
 *
 * Security:
 *   - No localStorage/sessionStorage (tokens in React state only)
 *   - Session expires in 24 hours
 *   - All document access goes through signed URLs (5-min expiry)
 *   - Every view is logged in portal_audit_log
 */
import { useState, useEffect, useCallback } from 'react';
import {
  FileText, Download, Clock, CheckCircle2, Phone, Mail,
  Home, Calendar, Shield, AlertCircle, Loader2, ArrowRight,
} from 'lucide-react';

// ── Inline styles (no theme import — this page is public) ────
const colors = {
  primary: '#1B65A6',
  text: '#1E293B',
  textLight: '#64748B',
  border: '#E2E8F0',
  bg: '#F8FAFC',
  card: '#FFFFFF',
  success: '#10B981',
  warning: '#F59E0B',
  accent: '#0EA5E9',
};

const styles = {
  page: { minHeight: '100vh', background: colors.bg, fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" },
  container: { maxWidth: 720, margin: '0 auto', padding: '24px 16px 60px' },
  card: { background: colors.card, borderRadius: 12, border: `1px solid ${colors.border}`, padding: 24, marginBottom: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
  h1: { fontSize: 22, fontWeight: 700, color: colors.text, margin: 0 },
  h2: { fontSize: 16, fontWeight: 600, color: colors.text, margin: '0 0 16px' },
  p: { fontSize: 14, color: colors.textLight, lineHeight: 1.6, margin: 0 },
  badge: { display: 'inline-block', padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600 },
};

const STAGE_LABELS = {
  attorney_review: 'Attorney Review',
  under_contract: 'Under Contract',
  inspection: 'Inspection',
  appraisal: 'Appraisal',
  mortgage_commitment: 'Mortgage Commitment',
  clear_to_close: 'Clear to Close',
  closed: 'Closed',
};

export default function ClientPortal() {
  const [sessionToken, setSessionToken] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Step 1: Exchange magic token for session
  useEffect(() => {
    (async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const magicToken = params.get('token');

        if (!magicToken) {
          setError('No access token provided. Please use the link from your email.');
          setLoading(false);
          return;
        }

        // Exchange magic token for session
        const authResp = await fetch('/api/client-portal', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ magic_token: magicToken }),
        });
        const authData = await authResp.json();
        if (!authResp.ok) throw new Error(authData.error || 'Failed to authenticate');

        setSessionToken(authData.session_token);

        // Clear token from URL for security (don't want it in browser history)
        window.history.replaceState({}, '', '/portal');

      } catch (e) {
        setError(e.message);
        setLoading(false);
      }
    })();
  }, []);

  // Step 2: Fetch deal data once we have a session
  useEffect(() => {
    if (!sessionToken) return;
    (async () => {
      try {
        const resp = await fetch(`/api/client-portal?session=${sessionToken}`);
        const d = await resp.json();
        if (!resp.ok) throw new Error(d.error || 'Failed to load deal');
        setData(d);
      } catch (e) {
        setError(e.message);
      } finally { setLoading(false); }
    })();
  }, [sessionToken]);

  const viewDocument = async (docId) => {
    if (!sessionToken) return;
    try {
      const resp = await fetch(`/api/client-portal?session=${sessionToken}&document_id=${docId}`);
      const d = await resp.json();
      if (!resp.ok) throw new Error(d.error);
      window.open(d.url, '_blank');
    } catch (e) {
      alert('Unable to open document: ' + e.message);
    }
  };

  // ── Loading ──
  if (loading) {
    return (
      <div style={{ ...styles.page, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <Loader2 size={32} style={{ color: colors.primary, animation: 'spin 1s linear infinite' }} />
          <p style={{ ...styles.p, marginTop: 12 }}>Loading your deal...</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
        </div>
      </div>
    );
  }

  // ── Error ──
  if (error) {
    return (
      <div style={{ ...styles.page, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <div style={{ ...styles.card, maxWidth: 420, textAlign: 'center' }}>
          <AlertCircle size={40} style={{ color: '#EF4444', marginBottom: 16 }} />
          <h2 style={{ ...styles.h1, marginBottom: 8 }}>Access Denied</h2>
          <p style={{ ...styles.p, marginBottom: 20 }}>{error}</p>
          <p style={{ ...styles.p, fontSize: 13 }}>
            Contact your agent to request a new link.
          </p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { deal, documents, timeline, agent, brokerage, client_name, client_role } = data;
  const brandColor = brokerage?.brand_color || colors.primary;

  return (
    <div style={styles.page}>
      <div style={styles.container}>

        {/* ── Header ── */}
        <div style={{ ...styles.card, borderTop: `4px solid ${brandColor}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
            <div>
              {brokerage?.logo_url ? (
                <img src={brokerage.logo_url} alt={brokerage.name} style={{ height: 32, marginBottom: 8 }} />
              ) : brokerage?.name ? (
                <div style={{ fontSize: 18, fontWeight: 700, color: brandColor, marginBottom: 8 }}>{brokerage.name}</div>
              ) : null}
              <h1 style={styles.h1}>
                <Home size={20} style={{ verticalAlign: 'middle', marginRight: 8, color: brandColor }} />
                {deal.property_address || 'Your Property'}
              </h1>
              {client_name && (
                <p style={{ ...styles.p, marginTop: 4 }}>
                  Welcome, {client_name} ({client_role === 'seller' ? 'Seller' : 'Buyer'})
                </p>
              )}
            </div>
            <div style={{ ...styles.badge, background: deal.status === 'closed' ? '#DCFCE7' : '#DBEAFE', color: deal.status === 'closed' ? '#166534' : '#1D4ED8' }}>
              {deal.status_label}
            </div>
          </div>

          {deal.offer_price && (
            <div style={{ display: 'flex', gap: 24, marginTop: 16, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: 12, color: colors.textLight, marginBottom: 2 }}>Contract Price</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: colors.text }}>${Number(deal.offer_price).toLocaleString()}</div>
              </div>
              {deal.closing_date && (
                <div>
                  <div style={{ fontSize: 12, color: colors.textLight, marginBottom: 2 }}>Target Close</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: colors.text }}>
                    {new Date(deal.closing_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Progress Bar ── */}
        <div style={styles.card}>
          <h2 style={styles.h2}>Deal Progress</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 0, overflowX: 'auto', paddingBottom: 4 }}>
            {(deal.stages || []).map((stage, i) => {
              const isCompleted = stage.completed;
              const isCurrent = stage.current;
              const isLast = i === deal.stages.length - 1;

              return (
                <div key={stage.key} style={{ display: 'flex', alignItems: 'center', flex: isLast ? '0 0 auto' : '1 1 auto' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 70 }}>
                    <div style={{
                      width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: isCompleted ? brandColor : isCurrent ? brandColor : '#E2E8F0',
                      color: isCompleted || isCurrent ? '#fff' : colors.textLight,
                      fontSize: 12, fontWeight: 600,
                      boxShadow: isCurrent ? `0 0 0 4px ${brandColor}33` : 'none',
                    }}>
                      {isCompleted ? <CheckCircle2 size={16} /> : i + 1}
                    </div>
                    <div style={{
                      fontSize: 10, fontWeight: isCurrent ? 600 : 400, marginTop: 4, textAlign: 'center',
                      color: isCurrent ? brandColor : isCompleted ? colors.text : colors.textLight,
                      maxWidth: 80, lineHeight: 1.2,
                    }}>
                      {stage.label}
                    </div>
                  </div>
                  {!isLast && (
                    <div style={{
                      flex: '1 1 auto', height: 2, minWidth: 16,
                      background: isCompleted ? brandColor : '#E2E8F0',
                      marginBottom: 18,
                    }} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Documents ── */}
        <div style={styles.card}>
          <h2 style={styles.h2}>
            <FileText size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
            Shared Documents
          </h2>
          {documents.length === 0 ? (
            <p style={{ ...styles.p, fontStyle: 'italic' }}>No documents have been shared yet. Your agent will share documents as they become available.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {documents.map(doc => (
                <div key={doc.id} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                  padding: '10px 14px', borderRadius: 8, border: `1px solid ${colors.border}`,
                  cursor: 'pointer', transition: 'background 0.15s',
                }}
                  onClick={() => viewDocument(doc.id)}
                  onMouseEnter={e => e.currentTarget.style.background = '#F0F7FF'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
                    <FileText size={18} style={{ color: brandColor, flexShrink: 0 }} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 500, color: colors.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {doc.file_name}
                      </div>
                      <div style={{ fontSize: 12, color: colors.textLight }}>
                        Shared {new Date(doc.shared_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        {doc.file_size ? ` · ${(doc.file_size / 1024).toFixed(0)} KB` : ''}
                      </div>
                    </div>
                  </div>
                  <Download size={16} style={{ color: colors.textLight, flexShrink: 0 }} />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Timeline ── */}
        {timeline.length > 0 && (
          <div style={styles.card}>
            <h2 style={styles.h2}>
              <Clock size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
              Timeline
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {timeline.map((event, i) => (
                <div key={event.id} style={{ display: 'flex', gap: 12, padding: '10px 0', borderBottom: i < timeline.length - 1 ? `1px solid ${colors.border}` : 'none' }}>
                  <div style={{
                    width: 8, height: 8, borderRadius: '50%', marginTop: 6, flexShrink: 0,
                    background: event.type === 'status_change' ? brandColor : event.type === 'signature_completed' ? colors.success : colors.accent,
                  }} />
                  <div>
                    <div style={{ fontSize: 14, color: colors.text }}>{event.description}</div>
                    <div style={{ fontSize: 12, color: colors.textLight, marginTop: 2 }}>
                      {new Date(event.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Agent Contact ── */}
        {agent && (
          <div style={styles.card}>
            <h2 style={styles.h2}>Your Agent</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              {agent.avatar_url ? (
                <img src={agent.avatar_url} alt={agent.name} style={{ width: 48, height: 48, borderRadius: '50%', objectFit: 'cover' }} />
              ) : (
                <div style={{ width: 48, height: 48, borderRadius: '50%', background: brandColor, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 18, fontWeight: 700 }}>
                  {(agent.name || '?')[0]}
                </div>
              )}
              <div>
                <div style={{ fontSize: 15, fontWeight: 600, color: colors.text }}>{agent.name}</div>
                {agent.title && <div style={{ fontSize: 13, color: colors.textLight }}>{agent.title}</div>}
                <div style={{ display: 'flex', gap: 16, marginTop: 6 }}>
                  {agent.phone && (
                    <a href={`tel:${agent.phone}`} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, color: brandColor, textDecoration: 'none' }}>
                      <Phone size={14} /> {agent.phone}
                    </a>
                  )}
                  {agent.email && (
                    <a href={`mailto:${agent.email}`} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, color: brandColor, textDecoration: 'none' }}>
                      <Mail size={14} /> Email
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Security notice ── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center', padding: '16px 0' }}>
          <Shield size={14} style={{ color: colors.textLight }} />
          <span style={{ fontSize: 12, color: colors.textLight }}>
            Secure portal · Read-only access · Session expires {data.session_expires_at ? new Date(data.session_expires_at).toLocaleString() : 'in 24 hours'}
          </span>
        </div>

        <div style={{ textAlign: 'center', fontSize: 12, color: colors.textLight }}>
          Powered by <a href="https://trovva.ai" style={{ color: brandColor, textDecoration: 'none', fontWeight: 600 }}>Trovva</a>
        </div>
      </div>
    </div>
  );
}
